import React, { forwardRef, useImperativeHandle } from "react";
import MUITextField from "@mui/material/TextField";
import HelperMessage from "@directv-digital/fusion.atoms.helper-message";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { ErrorIcon, MagnifyingGlassIcon } from "@directv-digital/fusion.atoms.icons";
import { FusionTextFieldProps, FusionTextFieldRef } from "./text-field.types";
import { textFieldStyles } from "./text-field.styles";
import { ThemeProvider } from "@mui/material/styles";
import FusionTheme from "@directv-digital/fusion.themes.fusion/";

function getFormattedPhoneNum(input: string): string {
  let output = "(";
  input.replace(/^\D*(\d{0,3})\D*(\d{0,3})\D*(\d{0,4})/, (_, g1, g2, g3): string => {
    if (g1?.length) {
      output += g1;
      // eslint-disable-next-line eqeqeq
      if (g1?.length == 3) {
        output += ")";
        if (g2?.length) {
          output += ` ${  g2}`;
          // eslint-disable-next-line eqeqeq
          if (g2?.length == 3) {
            output += " - ";
            if (g3?.length) {
              output += g3;
            }
          }
        }
      }
    }
    return output;
  });
  return output;
}

export const TextField = forwardRef<FusionTextFieldRef, FusionTextFieldProps>((
  {
    id,
    mode = "light",
    size = "medium",
    placeholder = "",
    label,
    defaultValue = "",
    disabled = false,
    required = false,
    iconVariant,
    width,
    value,
    leftIcon,
    rightIcon,
    onChange,
    onFocus,
    onBlur,
    onClear,
    onRightIconClick,
    rightIconAriaLabel,
    customRightIconVisible = false,
    customLeftIconVisible = false,
    customLabelShrink,
    showClearIcon = false,
    error = false,
    showHelperMessage = false,
    helperMessageText = "",
    helperMessageIcon = true,
    customHelperMessage = undefined,
    isVisible = true,
    ...otherProps
  },
  parentRef
) => {
  const [inputValue, setInputValue] = React.useState(defaultValue || "");
  const [focused, setFocused] = React.useState(false);

  const textFieldRef = React.useRef<HTMLInputElement>(null);

  // Allow parent components to control methods on the text field
  useImperativeHandle(parentRef, () => ({
    value: inputValue,
    focus: () => textFieldRef?.current?.focus(),
    blur: () => textFieldRef?.current?.blur(),
    clear: () => clearInput(),
    isFocused: focused
  }) as FusionTextFieldRef);

  const shrinkLabelActive = React.useMemo(() => (
    focused || inputValue !== "" || customLabelShrink || false
  ), [focused, inputValue, customLabelShrink]);

  const styles = React.useMemo(() => (
    textFieldStyles(mode, size, disabled, error, shrinkLabelActive)
  ), [mode, size, disabled, error, shrinkLabelActive])

  // Allow parent component to control the value of the text field
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  // const updateSelectionStart = () => {
  //   textFieldRef.current?.setSelectionRange(inputValue.length, inputValue.length);
  // };

  const clearInput = (e?: React.MouseEvent<HTMLButtonElement>) => {
    setInputValue("");
    if (e != null) {
      e.preventDefault();
    }
    if (onClear) {
      onClear(e);
    }
  };

  const handleRightIconClick = e => {
    if (onRightIconClick) {
      onRightIconClick(e);
    }

    if (textFieldRef?.current) {
      textFieldRef.current?.focus();
    }
  };

  const handleChange = (e?: any) => {
    e.target.value =
      otherProps?.type === "phonenumber" ?
        getFormattedPhoneNum(e.target.value) :
        otherProps?.type === "number" ?
          String(e?.target.value) :
          otherProps?.type === "zipcode" ?
            e?.target.value?.replace(/[^0-9.]/g, "")?.match(/\d{0,5}/)?.[0] :
            e?.target.value;

    setInputValue(e.target.value);

    if (onChange) {
      onChange(e);
    }
  };

  const handleFocus = (e?: React.FocusEvent<HTMLElement>) => {
    if (onFocus) {
      onFocus(e);
    }
    setFocused(true);
  };

  const handleBlur = (e?: React.FocusEvent<HTMLElement>) => {
    if (onBlur) {
      onBlur(e);
    }
    setFocused(false);
  };

  return (
    <ThemeProvider theme={FusionTheme}>
      <MUITextField
        FormHelperTextProps={{ component: "div" }}
        {...otherProps}
        disabled={disabled}
        error={error}
        id={id || `text-field-${label?.toLowerCase()?.replace(/\s+/g, "-")}`}
        inputRef={textFieldRef}
        label={label}
        placeholder={placeholder}
        required={required}
        type={otherProps?.type || "text"}
        value={inputValue || ""}
        variant="outlined"
        InputLabelProps={{
          shrink: shrinkLabelActive,
          sx: styles.InputLabel,
          style: styles.InputLabelStyle,
          ...otherProps?.InputLabelProps
        }}
        InputProps={{
          ...otherProps?.InputProps,
          onFocus: handleFocus,
          onBlur: handleBlur,
          tabIndex: isVisible ? 0 : -1,
          startAdornment:
            (iconVariant === "leftIcon" || iconVariant === "all") &&
            (focused || inputValue || customLeftIconVisible) && (
              <InputAdornment position="start">
                {leftIcon || (
                  <MagnifyingGlassIcon
                    fill={styles.SearchIconFill}
                  />
                )}
              </InputAdornment>
            ),
          endAdornment:
            (iconVariant === "rightIcon" || iconVariant === "clearIcon" || iconVariant === "all") &&
            (inputValue || customRightIconVisible) && (
              <InputAdornment position="end">
                {rightIcon && (
                  <IconButton
                    disableFocusRipple
                    sx={{
                      ...styles.AdornmentIcon
                    }}
                    aria-label={rightIconAriaLabel || `Click to clear ${label} input.`}
                    onClick={e => handleRightIconClick(e)}
                    disabled={disabled}
                  >
                    {rightIcon}
                  </IconButton>
                )}
                {showClearIcon && (
                  <IconButton
                    disableFocusRipple
                    sx={{
                      ...styles.AdornmentIcon,
                      ...!focused && {
                        opacity: 0,
                        pointerEvents: "none"
                      },
                      transition: "opacity 100ms ease-in-out"
                    }}
                    aria-label={`Click to clear ${label} input.`}
                    onMouseDown={e => {
                      clearInput(e);
                      textFieldRef?.current?.focus();
                    }}
                    disabled={disabled}
                  >
                    <ErrorIcon fill={styles.ClearIconFill} />
                  </IconButton>
                )}
              </InputAdornment>
            )
        }}
        onBlur={handleBlur}
        // onSelect={otherProps?.type !== "number" ? updateSelectionStart
        //   : () => { Promise.resolve() }
        // }
        sx={{
          width: width || {
 sm: "335px",
md: "335px",
lg: "270px"
},
          minWidth: width || {
 sm: "335px",
md: "335px",
lg: "270px"
},
          ...styles.InputContainer,
          ...otherProps?.sx
        }}
        onChange={handleChange}
        onFocus={handleFocus}
        {...otherProps}
      />
      {showHelperMessage && (
        customHelperMessage !== undefined ?
          customHelperMessage :
          <HelperMessage
              error={error}
              iconVisible={helperMessageIcon && !disabled}
              message={helperMessageText}
              mode={mode}
            />
      )}
    </ThemeProvider>
  );
});
